import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/toast';
import { useShiftMutations } from '@/features/shifts/hooks';
import type { Shift } from '@/features/shifts/schemas';

interface ShiftEditorProps {
  shift: Shift;
  defaultFixedValue: number;
  cnpjId: string | null;
}

export function ShiftEditor({ shift, defaultFixedValue, cnpjId }: ShiftEditorProps) {
  const { update } = useShiftMutations(cnpjId);
  const { show } = useToast();
  const [fixedValue, setFixedValue] = useState<string>(
    shift.overrideFixedValue == null ? '' : String(shift.overrideFixedValue),
  );
  const [notes, setNotes] = useState<string>(shift.notes ?? '');
  const [editing, setEditing] = useState(false);

  const save = (): void => {
    update.mutate(
      {
        id: shift.id,
        payload: {
          overrideFixedValue: fixedValue === '' ? null : Number(fixedValue),
          notes: notes || null,
        },
      },
      {
        onSuccess: () => {
          show({ title: 'Alterações salvas', variant: 'success' });
          setEditing(false);
        },
        onError: (e) =>
          show({
            title: 'Erro ao salvar',
            description: e instanceof Error ? e.message : '',
            variant: 'error',
          }),
      },
    );
  };

  if (!editing) {
    return (
      <section
        aria-label="Detalhes do plantão"
        className="rounded-md border border-(--color-border) bg-(--color-surface) p-4 text-sm"
      >
        <div className="flex items-center justify-between gap-2">
          <div>
            <p className="text-(--color-fg-muted)">Valor fixo deste plantão</p>
            <p className="font-medium tabular-nums">
              {brl(shift.overrideFixedValue ?? defaultFixedValue)}
              {shift.overrideFixedValue != null && (
                <span className="ml-2 text-xs text-(--color-fg-muted)">
                  (padrão: {brl(defaultFixedValue)})
                </span>
              )}
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
            Editar
          </Button>
        </div>
        {shift.notes && (
          <p className="mt-2 whitespace-pre-line text-(--color-fg-muted)">{shift.notes}</p>
        )}
      </section>
    );
  }

  return (
    <section className="flex flex-col gap-3 rounded-md border border-(--color-border) bg-(--color-surface) p-4 text-sm">
      <label className="flex flex-col gap-1">
        <span className="text-xs text-(--color-fg-muted)">Valor fixo (em branco = padrão)</span>
        <Input
          type="number"
          min={0}
          step="0.01"
          value={fixedValue}
          onChange={(e) => setFixedValue(e.target.value)}
        />
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-xs text-(--color-fg-muted)">Observações</span>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          className="rounded-md border border-(--color-border) bg-(--color-surface-elevated) p-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--color-accent)"
        />
      </label>
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={() => setEditing(false)}>
          Cancelar
        </Button>
        <Button size="sm" onClick={save} disabled={update.isPending}>
          {update.isPending ? 'Salvando…' : 'Salvar'}
        </Button>
      </div>
    </section>
  );
}

function brl(v: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
}
